<template>
  <div class="sidebar-pinned">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-file-text" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Claim Details</h4>
              <p class="mb-0 page-subtitle" v-if="claimdetails.claim_unique_id">
                {{ claimdetails.claim_unique_id }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5" id="printDiv" ref="printNow">

          <!-- Sent back to the facility for correction -->
          <div class="alert sent-back-alert mb-3" v-if="claimStatus.key === 'sent_back'">
            <div class="d-flex flex-wrap align-items-start" style="gap: 10px;">
              <i class="fe fe-corner-up-left mt-1"></i>
              <div class="flex-grow-1">
                <strong>Sent back to the facility for correction.</strong>
                <div v-if="sendBackNote" class="mt-1">{{ sendBackNote.body.replace(/^Sent back for correction:\s*/, '') }}
                  <small class="text-muted d-block">{{ sendBackNote.created_at | moment("MMM Do YYYY, h:mm a") }}</small>
                </div>
                <div v-if="isFacilityUser" class="small mt-1">Correct the flagged items (Billing, Documents), then click <b>Resubmit</b>. The claim goes back to pending.</div>
              </div>
              <button v-if="isFacilityUser" class="btn btn-sm btn-submit" @click="resubmitClaim" :disabled="reviewBusy">
                <i class="fe fe-send mr-1"></i> Resubmit
              </button>
            </div>
          </div>

          <div class="alert alert-warning mb-3" v-if="isDraftClaim">
            <i class="fe fe-edit-3 mr-1"></i> This claim is a <strong>draft</strong>. The agency can't see it
            until you click <strong>Submit for Vetting</strong>.
          </div>

          <!-- ── Action Toolbar ── -->
          <div class="card action-card shadow mb-4">
            <div class="card-body py-3 px-4">
              <div class="d-flex flex-wrap align-items-center" style="gap: 8px;">

                <!-- Primary workflow -->
                <button class="btn btn-submit btn-sm" @click="verifyClaim"
                  v-if="claimdetails.verified_by_id == null && user.job_title == 'Claims Verifier'">
                  <i class="fe fe-check mr-1"></i> Mark Verified
                </button>
                <button class="btn btn-submit btn-sm" @click="vetClaim"
                  v-if="claimdetails.checked_by_id == null && (user.job_title == 'Claims Vetter' || user.type == 'tpa_employee')">
                  <i class="fe fe-check mr-1"></i> Mark Vetted
                </button>
                <router-link :to="{ path: '/edit-claim/' + claimdetails.id }"
                  v-if="claimdetails.status == 0 && user.type == 'provider_employee'">
                  <button class="btn btn-outline-warning btn-sm">
                    <i class="fe fe-edit-2 mr-1"></i> Update Claim Application
                  </button>
                </router-link>
                <button class="btn btn-outline-warning btn-sm" @click="updateClaimStatus(-1, 'suspend')"
                  v-if="claimdetails.status != 1 && claimdetails.status != -1 && user.type != 'provider_employee' && user.type != 'provider'">
                  <i class="fe fe-pause-circle mr-1"></i> Suspend Claim
                </button>
                <button class="btn btn-outline-danger btn-sm" @click="updateClaimStatus(-2, 'reject')"
                  v-if="claimdetails.status != 1 && claimdetails.status != -2 && user.type != 'provider_employee' && user.type != 'provider'">
                  <i class="fe fe-x-circle mr-1"></i> Reject Claim
                </button>
                <button class="btn btn-outline-sent-back btn-sm" @click="openSendBack" v-if="canSendBack"
                  title="Return the claim to the facility to correct it; it comes back as pending when they resubmit">
                  <i class="fe fe-corner-up-left mr-1"></i> Send Back
                </button>

                <!-- Facility: draft claims (hidden from the agency until submitted) -->
                <button class="btn btn-submit btn-sm" @click="submitDraftClaim" :disabled="draftBusy"
                  v-if="isFacilityUser && isDraftClaim">
                  <i class="fe fe-send mr-1"></i> Submit for Vetting
                </button>
                <button class="btn btn-outline-secondary btn-sm" @click="returnClaimToDraft" :disabled="draftBusy"
                  v-if="isFacilityUser && canReturnToDraft">
                  <i class="fe fe-edit-3 mr-1"></i> Move Back to Draft
                </button>

                <!-- Tools -->
                <a target="_blank" :href="patientLink(claimdetails.enrollee)"
                  v-if="claimdetails.enrollee != null">
                  <button class="btn btn-tool btn-sm"><i class="fe fe-user mr-1"></i> Enrollee</button>
                </a>
                <button class="btn btn-tool btn-sm" @click="shownotes = !shownotes">
                  <i class="fe fe-clipboard mr-1"></i> Notes
                  <span class="badge badge-pill badge-secondary ml-1">{{ comments.length }}</span>
                </button>
                <button class="btn btn-tool btn-sm" @click="show = !show">
                  <i class="fe fe-plus mr-1"></i> Add Note
                </button>
                <router-link :to="{ path: '/upload-claims-docs/' + claimdetails.id }" v-if="claimdetails.status != 1">
                  <button class="btn btn-tool btn-sm">
                    <i class="fe fe-file-plus mr-1"></i> Documents
                    <span class="badge badge-pill badge-secondary ml-1">{{ singleclaim.docs && singleclaim.docs.length }}</span>
                  </button>
                </router-link>
                <router-link :to="{ path: '/service-processing-form/' + claimdetails.id }" v-if="claimdetails.status != 1">
                  <button class="btn btn-tool btn-sm"><i class="fe fe-thermometer mr-1"></i> Billing</button>
                </router-link>
                <router-link :to="{ path: '/encounter/' + claimdetails.servicesummary.id }"
                  v-if="claimdetails.servicesummary != null && claimdetails.status != 1">
                  <button class="btn btn-tool btn-sm"><i class="fe fe-activity mr-1"></i> Encounter</button>
                </router-link>
                <button class="btn btn-tool btn-sm" data-toggle="modal" data-target="#autoVetModal" @click="resetAutoVet">
                  <i class="fe fe-heart mr-1"></i> AI Vet
                </button>
                <button class="btn btn-tool btn-sm" @click="fetchClaimSummary" data-toggle="modal" data-target="#claimSummaryModal">
                  <i class="fe fe-bar-chart-2 mr-1"></i> Summary
                </button>
                <button class="btn btn-tool btn-sm" v-if="['shis', 'employee', 'nhia'].includes(user.type)" @click="activityOpen = true">
                  <i class="fe fe-clock mr-1"></i> Activity log
                </button>
                <!-- Claim-wide 10% drug deduction disabled: now applied per drug item from its side panel.
                <button class="btn btn-tool btn-sm" v-if="canDeductDrugs" @click="applyClaimDrugDeduction" :disabled="reviewBusy"
                  :title="drugDeductionLine ? 'Recalculates the existing 10% drug deduction instead of adding another one' : '10% of the drug amounts'">
                  <i class="fe fe-scissors mr-1"></i> {{ drugDeductionLine ? 'Update 10% Drug Deduction' : 'Deduct 10% Drugs' }}
                </button>
                <button class="btn btn-tool btn-sm" v-if="user.type != 'client'" @click="historyOpen = true">
                  <i class="fe fe-archive mr-1"></i> Previous Claims
                </button>
                -->
                <button class="btn btn-tool btn-sm" @click="printMe">
                  <i class="fe fe-printer mr-1"></i> Print
                </button>
              </div>
            </div>

            <!-- Add Note textarea -->
            <div class="card-body py-3 px-4 border-top" v-show="show">
              <label class="form-label">Leave a Comment</label>
              <textarea class="form-control mb-3" rows="4" v-model="body" placeholder="Write your note here…"></textarea>
              <button type="button" class="btn btn-submit btn-sm" @click="addComment">
                <i class="fe fe-send mr-1"></i> Submit
              </button>
            </div>
          </div>

          <!-- ── Comments (collapsible) ── -->
          <div v-show="shownotes" class="mb-4">
            <div v-if="comments && comments.length">
              <div v-for="comment in comments" :key="comment.id" class="media mb-3 p-3"
                style="background: #f8f9fa; border-left: 3px solid #5b8dee; border-radius: 4px;">
                <div class="avatar avatar-sm mr-3">
                  <div class="avatar-title rounded-circle bg-soft-info text-info" style="font-size: 0.75rem;">
                    <i class="fe fe-user"></i>
                  </div>
                </div>
                <div class="media-body">
                  <p class="mb-1">{{ comment.body }}</p>
                  <small class="text-muted">
                    <strong>{{ comment.user.full_name }}</strong>
                    &middot; {{ comment.user.type }}
                    &middot; {{ comment.created_at | moment("MMM Do YYYY, h:mm a") }}
                  </small>
                </div>
              </div>
            </div>
            <p v-else class="text-muted text-center py-3">No comments yet.</p>
          </div>

          <!-- ── Claim Info Cards ── -->
          <div class="row mb-4">
            <!-- Status badges -->
            <div class="col-12 mb-3">

              <div class="d-flex flex-wrap" style="gap: 6px;">
                <span class="badge px-3 py-2" :class="'badge-soft-' + claimStatus.variant" style="font-size: 0.8rem;">
                  <i :class="['fe', 'mr-1', claimStatus.icon]"></i> {{ claimStatus.label }}
                </span>
              </div>
            </div>

            <!-- Enrollee & Claim info -->
            <div class="col-md-6 mb-4">
              <div class="card info-card h-100">
                <div class="info-card-header">
                  <i class="fe fe-user mr-2"></i> Enrollee Information
                </div>
                <div v-if="claimdetails.enrollee != null" class="text-center py-3"
                  style="background: #f7f9ff; border-bottom: 1px solid #e9ecef;">
                  <a v-if="claimdetails.enrollee.user_image" target="_blank" :href="patientLink(claimdetails.enrollee)" title="Open patient page">
                    <img
                      :src="claimdetails.enrollee.image_url" class="rounded-circle text-center mx-auto d-block"
                      style="width: 80px; height: 80px; object-fit: cover; border: 3px solid #2563eb;"
                      onerror="this.onerror=null; this.src='/assets/img/claimify_logo.png'" />
                  </a>
                  <div v-else class="rounded-circle mx-auto d-flex align-items-center justify-content-center"
                    style="width: 80px; height: 80px; background: #eef2ff; border: 3px solid #c7d2e8;">
                    <i class="fe fe-user" style="font-size: 2rem; color: #2563eb;"></i>
                  </div>
                  <p class="mb-0 font-weight-bold mt-2" style="font-size: 0.9rem;">
                    {{ claimdetails.enrollee.full_name }}
                  </p>
                  <small class="text-muted">{{ claimdetails.enrollee.id_card_number }}</small>
                </div>
                <div class="list-group list-group-flush">
                  <div class="list-group-item px-3 py-2 d-flex align-items-start" v-if="claimdetails.enrollee != null">
                    <i class="fe fe-user text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Name</p>
                      <a target="_blank" :href="patientLink(claimdetails.enrollee)">
                        <p class="mb-0 font-weight-bold">
                          {{ claimdetails.enrollee.full_name }} ({{ claimdetails.enrollee.id_card_number }})
                        </p>

                        <p>{{ claimdetails.enrollee.sector }}</p>
                      </a>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start" v-if="claimdetails.enrollee != null">
                    <i class="fe fe-calendar text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Date of Birth</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.enrollee.dob }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start" v-if="claimdetails.enrollee != null">
                    <i class="fe fe-phone text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Phone Number</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.enrollee.phone_number }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start" v-if="claimdetails.enrollee != null">
                    <i class="fe fe-user-check text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Enrolled At</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.enrollee.created_at }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-home text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Health Facility</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.provider.agency_name }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-navigation text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Out of Station</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.is_out_of_station ? 'Yes' : 'No' }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start" v-if="claimdetails.auth_code != null">
                    <i class="fe fe-key text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Authorization Code
                      </p>
                      <a :href="`/authorization-code/${claimdetails.auth_code.id}`" target="_blank"
                        class="font-weight-bold">
                        {{ claimdetails.authorization_code }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Claim details -->
            <div class="col-md-6 mb-4">
              <div class="card info-card h-100">
                <div class="info-card-header">
                  <i class="fe fe-file-text mr-2"></i> Claim Details
                </div>
                <div class="list-group list-group-flush">
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-alert-circle text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Diagnosis</p>
                      <p class="mb-0 font-weight-bold">
                        <span v-if="claimdetails.diagnosis.diagnosis_code" class="text-muted font-weight-normal mr-1">({{ claimdetails.diagnosis.diagnosis_code }})</span>{{ claimdetails.diagnosis.name }}
                      </p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-message-square text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Reason for Claim</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.treatment }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-layers text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Treatment Level</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.claim_level }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start" v-if="singleclaim.encounter">
                    <i class="fe fe-activity text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Encounter Record</p>
                      <a class="mb-0 font-weight-bold text-info" target="_blank"
                        :href="`/encounter/${singleclaim.encounter.id}`">#{{ singleclaim.encounter.id }}</a>
                      <p class="mb-0 text-muted" style="font-size: 0.8rem;">
                        Admitted {{ singleclaim.encounter.date_of_admission }} &middot; Discharged {{ singleclaim.encounter.date_of_discharge }}
                      </p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-user-check text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Prepared By</p>
                      <p class="mb-0 font-weight-bold">{{ prepared_by.full_name }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start">
                    <i class="fe fe-clock text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Date Created</p>
                      <p class="mb-0 font-weight-bold">{{ claimdetails.created_at | moment("ddd, MMM Do YYYY") }}</p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start"
                    v-if="claimdetails.checked_by_id != null">
                    <i class="fe fe-shield text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Vetted By · Date</p>
                      <p class="mb-0 font-weight-bold">
                        {{ claimdetails.checkeduser.full_name }}
                        <span class="text-muted font-weight-normal">· {{ claimdetails.checked_by_date }}</span>
                      </p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start"
                    v-if="claimdetails.verified_by_id != null">
                    <i class="fe fe-check-square text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Verified By · Date
                      </p>
                      <p class="mb-0 font-weight-bold">
                        {{ claimdetails.verfieduser.full_name }}
                        <span class="text-muted font-weight-normal">· {{ claimdetails.verified_by_date }}</span>
                      </p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start"
                    v-if="claimdetails.approved_by_id != null">
                    <i class="fe fe-check-circle text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Approved By · Date
                      </p>
                      <p class="mb-0 font-weight-bold">
                        {{ claimdetails.approveduser.full_name }}
                        <span class="text-muted font-weight-normal">· {{ claimdetails.approved_by_date }}</span>
                      </p>
                    </div>
                  </div>
                  <div class="list-group-item px-3 py-2 d-flex align-items-start"
                    v-if="claimdetails.paymentorders.length >= 1">
                    <i class="fe fe-credit-card text-muted mr-3 mt-1" style="min-width:16px;"></i>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size:0.72rem; text-transform:uppercase; letter-spacing:.04em;">Payment Order</p>
                      <a :href="`/transaction/${claimdetails.paymentorders[0].id}`" class="font-weight-bold text-info">
                        {{ claimdetails.paymentorders[0].payment_number }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Fraud signals (agency staff) ── -->
          <FraudSignals :claim-id="$route.params.id" />

          <!-- ── Services / Drugs Table ── -->
          <div class="card info-card mb-4">
            <div class="info-card-header d-flex align-items-center justify-content-between">
              <span><i class="fe fe-thermometer mr-2"></i> Services / Drugs</span>
              <button
                v-if="(user.type == 'shis' || user.type == 'employee') && claimdetails.status != 1"
                class="btn btn-sm btn-accept-all"
                @click="acceptAllVetAmounts"
              >
                <i class="fe fe-check-circle mr-1"></i> Accept All Vet Amounts
              </button>
            </div>
            <div class="card-body p-0">
              <!-- Bulk actions on the ticked items -->
              <div class="bulk-bar" v-if="canReviewLines && selectedLines.length">
                <span class="font-weight-bold">{{ selectedLines.length }} item(s) selected</span>
                <select class="form-control form-control-sm" style="width: auto; min-width: 200px;" v-model="bulkRemark">
                  <option value="">Rejection reason…</option>
                  <option :value="remark.id" v-for="remark in remarks" :key="remark.id">{{ remark.name }}</option>
                </select>
                <input type="text" class="form-control form-control-sm" style="max-width: 260px;" v-model="bulkComment"
                  placeholder="Comment (optional)" />
                <button class="btn btn-sm btn-danger" @click="rejectSelectedLines" :disabled="reviewBusy">
                  <i class="fe fe-x-circle mr-1"></i> Reject selected
                </button>
                <button class="btn btn-sm btn-outline-sent-back" v-if="canSendBack" @click="openSendBack">
                  <i class="fe fe-corner-up-left mr-1"></i> Send back with these
                </button>
                <button class="btn btn-sm btn-link" @click="selectedLines = []">Clear</button>
              </div>
              <div class="table-responsive">
                <table class="table align-td-middle table-card">
                  <thead>
                    <tr>
                      <th v-if="canReviewLines" style="width: 36px;">
                        <input type="checkbox" :checked="allLinesSelected" @change="toggleAllLines($event.target.checked)"
                          aria-label="Select all items" />
                      </th>
                      <th>#</th>
                      <th>Name</th>
                      <th>Requested Cost</th>
                      <th>Vetted Amount</th>
                      <th>Verified Amount</th>
                      <th>Verdict</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(service, index) in singleclaim.services" :key="service.id"
                      :class="{ 'row-selected': selectedLines.includes(service.id) }">
                      <td v-if="canReviewLines">
                        <input type="checkbox" :value="service.id" v-model="selectedLines" :aria-label="'Select item ' + (index + 1)" />
                      </td>
                      <td>{{ index + 1 }}</td>
                      <td>
                        <span v-if="service.service != null">{{ service.service.description }}</span>
                        <span v-if="service.drug != null">{{ service.drug.drug_name }}</span>
                      </td>
                      <td>&#8358;{{ service.cost | numeral(0, 0) }}</td>
                      <td>
                        <span v-show="listprice">&#8358;{{ service.vetted_price | numeral(0, 0) }}</span>
                        <div class="btn-group CTAs" v-if="service.ai != null">
                          <button type="button" class="btn btn-info badge dropdown-toggle" data-toggle="dropdown"
                            aria-haspopup="true" aria-expanded="false">
                            ₦{{ service.ai.vetted_price | numeral(0, 0) }}
                          </button>
                          <div class="dropdown-menu">
                            <button class="btn btn-success btn-block badge" @click="updateServicePrice(service)">
                              Accept <i class="fe fe-check"></i>
                            </button>
                            <button class="btn btn-danger btn-block badge" @click="updateprice_vet = true">
                              Reject <i class="fe fe-x"></i>
                            </button>
                          </div>
                        </div>
                        <input type="text" class="form-control m-2" v-show="updateprice_vet"
                          @change="updateServicePrice(service)" v-model="service.vetted_price" />
                      </td>
                      <td>
                        <span v-show="listprice">&#8358;{{ service.verified_price | numeral(0, 0) }}</span>
                        <input type="text" class="form-control" :id="`price${service.id}`" v-show="updateprice_ver"
                          @change="updateServicePrice(service)" v-model="service.verified_price" />
                      </td>
                      <td>
                        <span v-if="service.remark != null">{{ service.remark.name }}</span>
                      </td>
                      <td>
                        <button data-toggle="modal" data-target="#slideRightModalAll"
                          class="btn btn-sm btn-outline-info mr-1" @click="getSingleService(service)">
                          <i class="fe fe-eye"></i>
                        </button>
                        <div class="d-inline-block" v-if="claimdetails.status != 1">
                          <div v-if="service.id == rejectingServiceId" class="mt-2" style="min-width: 200px;">
                            <label class="small font-weight-bold text-danger mb-1 d-block">Rejection Remark
                              <span class="text-danger">*</span></label>
                            <select class="form-control form-control-sm mb-2" v-model="remark_selected">
                              <option value="">-- Select reason --</option>
                              <option :value="remark.id" v-for="remark in remarks" :key="remark.id">{{ remark.name }}
                              </option>
                            </select>
                            <div class="d-flex" style="gap: 4px;">
                              <button class="btn btn-sm btn-danger" @click="confirmRejectService(service)">
                                <i class="fe fe-x-circle mr-1"></i> Confirm Reject
                              </button>
                              <button class="btn btn-sm btn-outline-secondary" @click="cancelRejectService">
                                Cancel
                              </button>
                            </div>
                          </div>
                          <div v-else-if="service.id == selected_service" class="form-group mt-2 mb-0"
                            style="min-width: 160px;">
                            <select class="form-control form-control-sm" v-model="remark_selected"
                              @change="updateService(service)">
                              <option :value="remark.id" v-for="remark in remarks" :key="remark.id">{{ remark.name }}
                              </option>
                            </select>
                          </div>
                          <div v-else-if="user.type != 'provider_employee' && user.type != 'provider'"
                            class="btn-group">
                            <button type="button" class="btn btn-sm btn-outline-secondary dropdown-toggle"
                              data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                              <i class="fe fe-more-vertical"></i>
                            </button>
                            <div class="dropdown-menu dropdown-menu-right">
                              <a class="dropdown-item" role="button" @click="remarkService(service)">
                                <i class="fe fe-tag mr-1"></i> Make Verdict
                              </a>
                              <a class="dropdown-item text-danger" role="button" @click="startRejectService(service)"
                                v-if="user.job_title == 'Claims Vetter' || user.type == 'tpa_employee' || user.type == 'shis' || user.type == 'employee'">
                                <i class="fe fe-x-circle mr-1"></i> Reject Service
                              </a>
                              <a class="dropdown-item" role="button" @click="readycommentService(service)">
                                <i class="fe fe-message-square mr-1"></i> Add Comment
                              </a>
                              <div class="dropdown-divider"></div>
                              <template v-if="user.job_title == 'Claims Vetter' || user.type == 'tpa_employee'">
                                <a class="dropdown-item" role="button" @click="updateprice_vet = true">
                                  <i class="fe fe-edit-2 mr-1"></i> Vet Amount
                                </a>
                                <a class="dropdown-item text-warning" role="button"
                                  v-if="service.drug != null && Number(service.vetted_price) > 0"
                                  @click="applyDrugDeduction(service)">
                                  <i class="fe fe-percent mr-1"></i> Apply 10% Drug Deduction
                                </a>
                                <a class="dropdown-item text-success" role="button"
                                  @click="acceptServiceCost(service, 'vet')">
                                  <i class="fe fe-check mr-1"></i> Accept Requested Cost
                                </a>
                              </template>
                              <template v-if="user.type == 'shis' || user.type == 'employee'">
                                <div class="dropdown-divider"></div>
                                <a class="dropdown-item" role="button" @click="updateprice_ver = true">
                                  <i class="fe fe-edit-2 mr-1"></i> Verify Amount
                                </a>
                                <a class="dropdown-item text-success" role="button"
                                  @click="acceptServiceCost(service, 'verify')">
                                  <i class="fe fe-check mr-1"></i> Accept Requested Cost
                                </a>
                                <a class="dropdown-item text-primary font-weight-bold" role="button"
                                  @click="acceptVetAmount(service)">
                                  <i class="fe fe-check-circle mr-1"></i> Accept Vet Amount
                                </a>
                              </template>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr class="bg-light font-weight-bold">
                      <td :colspan="canReviewLines ? 3 : 2">Total</td>
                      <td>&#8358;{{ singleclaim.sum | numeral(0, 0) }}</td>
                      <td>&#8358;{{ singleclaim.vetted_sum | numeral(0, 0) }}</td>
                      <td>&#8358;{{ singleclaim.verified_sum | numeral(0, 0) }}</td>
                      <td colspan="2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- ── Documents Table ── -->
          <div class="card info-card mb-4">
            <div class="info-card-header">
              <i class="fe fe-file mr-2"></i> Claim Documents
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table align-td-middle table-card">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Uploaded By</th>
                      <th>Date Uploaded</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(doc, index) in singleclaim.docs" :key="doc.id">
                      <td>{{ index + 1 }}</td>
                      <td>{{ doc.name }}</td>
                      <td>{{ doc.user.full_name }}</td>
                      <td>{{ doc.created_at | moment("MMM Do YYYY") }}</td>
                      <td>
                        <a :href="'https://claimify-api.hayokmedicare.ng/documents/' + doc.document" target="_blank">
                          <button class="btn btn-sm btn-outline-info">
                            <i class="fe fe-eye"></i>
                          </button>
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

      </div><!-- end container -->

      <!-- ── Service Detail Modal ── -->
        <div class="modal fade modal-slide-right" id="slideRightModalAll" tabindex="-1" role="dialog"
          aria-labelledby="slideRightModalLabel" aria-hidden="true">
          <div class="modal-dialog scrollmodal" role="document">
            <div class="modal-content">
              <div class="modal-header bg-soft-info" style="border-bottom: 2px solid #e3eaf0;">
                <h5 class="modal-title" id="slideRightModalLabel">
                  <span v-if="singlservicemodal.service != null">{{ singlservicemodal.service.description }}</span>
                  <span v-if="singlservicemodal.drug != null">{{ singlservicemodal.drug.drug_name }}: {{
                    singlservicemodal.drug.item_data }}</span>
                </h5>
                <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="modal-body">
                <div class="list-group list-group-flush mb-3">
                  <div class="list-group-item px-0 py-2">
                    <small class="text-muted text-uppercase">Unit Amount</small>
                    <p class="mb-0 font-weight-bold">
                      &#8358;
                      <span v-if="singlservicemodal.service != null">{{ singlservicemodal.service.price }}</span>
                      <span v-if="singlservicemodal.drug != null">{{ singlservicemodal.drug.price }}</span>
                    </p>
                  </div>
                  <div class="list-group-item px-0 py-2">
                    <small class="text-muted text-uppercase"> <span v-if="singlservicemodal.drug"> Dose / </span>
                      Frequency / Days</small>
                    <p class="mb-0 font-weight-bold">
                    <span v-if="singlservicemodal.drug"> {{ singlservicemodal.dose }} · </span>   {{ singlservicemodal.frequency }}x/day · {{ singlservicemodal.days
                      }} days
                    </p>
                  </div>
                  <div class="list-group-item px-0 py-2">
                    <small class="text-muted text-uppercase">Verdict</small>
                    <p class="mb-0 font-weight-bold">
                      <span v-if="singlservicemodal.remark != null">{{ singlservicemodal.remark.name }}</span>
                      <span v-else class="text-muted">—</span>
                    </p>
                  </div>
                  <div class="list-group-item px-0 py-2">
                    <small class="text-muted text-uppercase">Requested Amount</small>
                    <p class="mb-0 font-weight-bold">&#8358;{{ singlservicemodal.vetted_price }}</p>
                  </div>
                  <div class="list-group-item px-0 py-2">
                    <small class="text-muted text-uppercase">Approved Amount</small>
                    <p class="mb-0 font-weight-bold">&#8358;{{ singlservicemodal.verified_price }}</p>
                  </div>
                </div>

                <!-- How often this patient has had this service / drug -->

                <ServiceUsage v-if="claimdetails && claimdetails.id" :claim-id="claimdetails.id" :line-id="singlservicemodal && singlservicemodal.id" />


                <!-- Verdict -->
                <div class="card mb-3" style="border-left: 4px solid #5b8dee;"
                  v-if="claimdetails.status != 1 && user.type != 'provider_employee' && user.type != 'provider'">
                  <div class="card-body py-3">
                    <p class="mb-2 font-weight-bold" style="font-size: 0.85rem;">Verdict</p>
                    <select class="form-control form-control-sm mb-2" v-model="modalVerdict">
                      <option value="">-- Select verdict --</option>
                      <option :value="remark.id" v-for="remark in remarks" :key="remark.id">{{ remark.name }}</option>
                    </select>
                    <button class="btn btn-info btn-block btn-sm" @click="saveModalVerdict">
                      <i class="fe fe-tag mr-1"></i> Save Verdict
                    </button>
                  </div>
                </div>

                <!-- Price Calculator -->
                <div class="card mb-3" style="border-left: 4px solid #5b8dee;"
                  v-if="claimdetails.status != 1 && user.type != 'provider_employee' && user.type != 'provider'">
                  <div class="card-body py-3">
                    <p class="mb-2 font-weight-bold" style="font-size: 0.85rem;">Price Calculator</p>
                    <table class="table table-sm mb-2">
                      <tbody>
                        <tr v-if="singlservicemodal.drug != null">
                          <td>Dose</td>
                          <td><input type="number" class="form-control form-control-sm" v-model="calc.dose" /></td>
                        </tr>
                        <tr>
                          <td>Frequency</td>
                          <td><input type="number" class="form-control form-control-sm" v-model="calc.frequency" /></td>
                        </tr>
                        <tr>
                          <td>Days</td>
                          <td><input type="number" class="form-control form-control-sm" v-model="calc.days" /></td>
                        </tr>
                        <tr class="bg-light">
                          <td><strong>Total</strong></td>
                          <td><strong>&#8358;{{ calculateTotal | numeral(0, 0) }}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                    <div class="d-flex" style="gap: 6px;">
                      <button class="btn btn-outline-info btn-block btn-sm"
                        v-if="user.job_title == 'Claims Vetter' || user.type == 'tpa_employee'"
                        @click="applyCalcToPrice('vetted_price')">
                        <i class="fe fe-shield mr-1"></i> Apply to Vetted
                      </button>
                      <button class="btn btn-outline-warning btn-block btn-sm"
                        v-if="singlservicemodal.drug != null && Number(singlservicemodal.vetted_price) > 0 && (user.job_title == 'Claims Vetter' || user.type == 'tpa_employee')"
                        @click="applyDrugDeduction(singlservicemodal)">
                        <i class="fe fe-percent mr-1"></i> Deduct 10%
                      </button>
                      <button class="btn btn-outline-info btn-block btn-sm"
                        v-if="user.type == 'shis' || user.type == 'employee'"
                        @click="applyCalcToPrice('verified_price')">
                        <i class="fe fe-check-square mr-1"></i> Apply to Verified
                      </button>
                    </div>

                    <hr class="my-3" />

                    <div class="custom-control custom-switch">
                      <input type="checkbox" class="custom-control-input" id="manualPriceSwitch"
                        v-model="showManualPrice" />
                      <label class="custom-control-label font-weight-bold" for="manualPriceSwitch"
                        style="font-size: 0.85rem;">
                        Set Amount Manually
                      </label>
                    </div>
                    <div class="mt-2" v-show="showManualPrice">
                      <div class="form-group mb-2" v-if="user.job_title == 'Claims Vetter' || user.type == 'tpa_employee'">
                        <label class="small text-muted mb-1">Vetted Amount</label>
                        <div class="input-group input-group-sm">
                          <input type="number" class="form-control" v-model="singlservicemodal.vetted_price" />
                          <div class="input-group-append">
                            <button class="btn btn-info" @click="updateServicePrice(singlservicemodal)">Save</button>
                          </div>
                        </div>
                      </div>
                      <div class="form-group mb-0" v-if="user.type == 'shis' || user.type == 'employee'">
                        <label class="small text-muted mb-1">Verified Amount</label>
                        <div class="input-group input-group-sm">
                          <input type="number" class="form-control" v-model="singlservicemodal.verified_price" />
                          <div class="input-group-append">
                            <button class="btn btn-info" @click="updateServicePrice(singlservicemodal)">Save</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Service Comments -->
                <p class="font-weight-bold mb-2">Comments</p>
                <div v-for="comment in singlservicemodal.servicecomments" :key="comment.id" class="media mb-2 p-2"
                  style="background: #f8f9fa; border-radius: 4px;">
                  <div class="media-body">
                    <p class="mb-1" style="font-size: 0.85rem;">{{ comment.body }}</p>
                    <small class="text-muted">
                      <strong>{{ comment.user.full_name }}</strong> · {{ comment.created_at | moment("from", "now") }}
                    </small>
                  </div>
                </div>
                <div v-if="!singlservicemodal.servicecomments || !singlservicemodal.servicecomments.length"
                  class="text-muted text-center py-2" style="font-size: 0.85rem;">
                  No comments yet.
                </div>

                <!-- Add Comment -->
                <div class="mt-2" v-if="claimdetails.status != 1 && user.type != 'provider_employee' && user.type != 'provider'">
                  <textarea class="form-control form-control-sm mb-2" rows="3" v-model="modalCommentBody"
                    placeholder="Write a comment on this item…"></textarea>
                  <button class="btn btn-info btn-block btn-sm" @click="addModalComment">
                    <i class="fe fe-send mr-1"></i> Add Comment
                  </button>
                </div>
              </div>
              <div class="modal-footer" style="border-top: 2px solid #e3eaf0;">
                <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">Close</button>
              </div>
            </div>
          </div>
        </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
      <ActivityLogPanel v-if="claimdetails && claimdetails.id" v-model="activityOpen" type="claim" :record-id="claimdetails.id"
        :subtitle="'Claim ' + (claimdetails.claim_unique_id || '#' + claimdetails.id) + ': who did what and when'" />
      <ClaimHistoryPanel v-if="claimdetails && claimdetails.id" v-model="historyOpen" :claim-id="claimdetails.id"
        :subtitle="(claimdetails.enrollee ? claimdetails.enrollee.full_name + ' · ' + (claimdetails.enrollee.id_card_number || '') : 'This patient') + ': earlier claims and their items'" />

      <!-- Send back to the facility -->
      <div v-if="sendBackOpen" class="sb-backdrop" @click.self="sendBackOpen = false">
        <div class="sb-dialog" role="dialog" aria-modal="true" aria-labelledby="sb-title">
          <div class="sb-head">
            <h6 id="sb-title" class="mb-0 font-weight-bold text-white"><i class="fe fe-corner-up-left mr-2"></i> Send claim back to the facility</h6>
            <button type="button" class="close text-white" aria-label="Close" @click="sendBackOpen = false"><span aria-hidden="true">&times;</span></button>
          </div>
          <div class="p-3">
            <p class="small text-muted mb-2">
              The facility is told in chat and sees your note on the claim. It corrects the claim and resubmits it, and the
              claim comes back as <b>pending</b> for vetting. Vetting and verification marks are cleared.
            </p>
            <label class="form-label">What needs to be corrected? <span class="text-danger">*</span></label>
            <textarea class="form-control mb-2" rows="4" v-model="sendBackReason"
              placeholder="e.g. Attach the lab result for the malaria test and correct the ACT quantity"></textarea>
            <div v-if="selectedLines.length" class="small mb-2">
              <i class="fe fe-tag mr-1"></i> The note is also added to the {{ selectedLines.length }} ticked item(s):
              <span class="text-muted">{{ selectedLineNames.join(", ") }}</span>
            </div>
            <div v-else class="small text-muted mb-2">Tip: tick items in the table first to flag them for the facility.</div>
            <div class="d-flex justify-content-end" style="gap: 8px;">
              <button class="btn btn-outline-secondary btn-sm" @click="sendBackOpen = false">Cancel</button>
              <button class="btn btn-sent-back btn-sm" @click="sendBackClaim" :disabled="reviewBusy || sendBackReason.trim().length < 3">
                <i class="fe fe-send mr-1"></i> Send back
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
    <Footer />

    <!-- ── AI Auto Vetting Modal ── -->
    <div class="modal fade modal-slide-right" id="autoVetModal" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog scrollmodal" role="document" style="width: 600px; max-width: 100%;">
        <div class="modal-content">
          <div class="modal-header bg-soft-info" style="border-bottom: 2px solid #e3eaf0;">
            <div class="d-flex align-items-center">
              <div class="avatar avatar-sm mr-3">
                <div class="avatar-title rounded-circle bg-info text-white"><i class="fe fe-heart"></i></div>
              </div>
              <h5 class="modal-title mb-0">AI Auto Vetting</h5>
            </div>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <!-- Layer Selection -->
            <div class="card mb-3" style="border-left: 4px solid #5b8dee;">
              <div class="card-body py-3">
                <p class="mb-2 font-weight-bold" style="font-size: 0.85rem;">
                  <i class="fe fe-sliders mr-1 text-info"></i> Select Vetting Layers
                </p>
                <div class="custom-control custom-checkbox mb-2">
                  <input type="checkbox" class="custom-control-input" id="layerA" value="A" v-model="vetLayers" />
                  <label class="custom-control-label" for="layerA">
                    <strong>A — Historical Benchmarking</strong>
                    <span class="text-muted d-block" style="font-size: 0.78rem;">Compares submitted price against past
                      verified prices for same diagnosis, age group and gender.</span>
                  </label>
                </div>
                <div class="custom-control custom-checkbox mb-3" v-if="canUseHiva">
                  <input type="checkbox" class="custom-control-input" id="layerC" value="C" v-model="vetLayers" />
                  <label class="custom-control-label" for="layerC">
                    <strong>HIVA Trace AI Clinical Review</strong>
                    <span class="text-muted d-block" style="font-size: 0.78rem;">Sends the claim, its drugs and services
                      and the enrollee's coverage (no names or phone numbers) to HIVA TRACE for price, dosage,
                      eligibility and drug checks.</span>
                  </label>
                </div>

                <!-- HIVA TRACE options -->
                <div v-if="canUseHiva && vetLayers.includes('C')" class="hiva-options mb-3">
                  <p class="mb-1 text-muted" style="font-size: 0.72rem; text-transform: uppercase;">HIVA TRACE checks</p>
                  <div class="d-flex flex-wrap mb-2" style="gap: 12px;">
                    <div class="custom-control custom-checkbox" v-for="opt in hivaCheckOptions" :key="opt.value">
                      <input type="checkbox" class="custom-control-input" :id="'hivaCheck-' + opt.value" :value="opt.value"
                        v-model="hivaChecks" />
                      <label class="custom-control-label" :for="'hivaCheck-' + opt.value" style="font-size: 0.8rem;">
                        {{ opt.label }}</label>
                    </div>
                  </div>
                  <div class="custom-control custom-switch mb-1">
                    <input type="checkbox" class="custom-control-input" id="hivaIncludeAi" v-model="hivaIncludeAi" />
                    <label class="custom-control-label" for="hivaIncludeAi" style="font-size: 0.8rem;">
                      Include AI recommendation <span class="text-muted">(slower)</span></label>
                  </div>
                  <div class="custom-control custom-switch">
                    <input type="checkbox" class="custom-control-input" id="hivaForce" v-model="hivaForce" />
                    <label class="custom-control-label" for="hivaForce" style="font-size: 0.8rem;">
                      Run again even if vetted in the last hour</label>
                  </div>
                  <small class="text-muted d-block mt-1" style="font-size: 0.72rem;">
                    <i class="fe fe-info mr-1"></i>Each HIVA TRACE run uses agency credit. An unchanged claim vetted in the
                    last hour shows the saved result instead.
                  </small>
                </div>
                <button class="btn btn-info btn-block" @click="runAutoVet" :disabled="vetLoading || !vetLayers.length">
                  <span v-if="vetLoading"><span class="spinner-border spinner-border-sm mr-1"></span> Analysing…</span>
                  <span v-else><i class="fe fe-play mr-1"></i> Run Analysis</span>
                </button>
              </div>
            </div>

            <!-- HIVA TRACE: previous results for this claim -->
            <div class="card mb-3" v-if="canUseHiva && (hivaHistoryLoading || hivaHistory.length)">
              <div class="card-header py-2 d-flex align-items-center justify-content-between">
                <span class="font-weight-bold" style="font-size: 0.82rem;">
                  <i class="fe fe-clock text-info mr-1"></i> Previous HIVA TRACE results
                </span>
                <span v-if="hivaHistoryLoading" class="spinner-border spinner-border-sm text-muted"></span>
              </div>
              <ul class="list-group list-group-flush">
                <li v-for="v in hivaHistory.slice(0, 5)" :key="v.id"
                  class="list-group-item py-2 d-flex align-items-center justify-content-between"
                  :class="{ 'bg-soft-info': hivaView && hivaView.id === v.id }" style="font-size: 0.78rem;">
                  <div>
                    <span v-if="v.error" class="badge badge-soft-danger mr-1">Error</span>
                    <span v-else :class="hivaBadge(v.verdict)" class="mr-1">{{ v.verdict || '—' }}</span>
                    {{ v.created_at }}
                    <span class="text-muted" v-if="v.requester"> · {{ v.requester.full_name }}</span>
                    <span class="text-muted" v-if="!v.error && !v.ai_included"> · no AI</span>
                    <span v-if="v.error" class="d-block text-danger" style="font-size: 0.72rem;">{{ v.error }}</span>
                  </div>
                  <button v-if="!v.error && v.response" class="btn btn-link btn-sm p-0" @click="viewHivaResult(v)">
                    {{ hivaView && hivaView.id === v.id ? 'Hide' : 'View' }}
                  </button>
                </li>
              </ul>
            </div>

            <!-- HIVA TRACE error (the other layers' results still show) -->
            <div v-if="vetResults && vetResults.external_api_error" class="alert alert-danger mb-3" style="font-size: 0.82rem;">
              <i class="fe fe-alert-triangle mr-1"></i>
              <strong>HIVA TRACE:</strong> {{ vetResults.external_api_error.message }}
            </div>

            <!-- Results -->
            <div v-if="vetResults || hivaReport">
              <div class="row mb-3" v-if="vetResults">
                <div class="col-4">
                  <div class="card text-center py-2">
                    <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Auto-Approved</p>
                    <h4 class="mb-0 text-success">{{ vetResults.summary.items_auto_approved }}</h4>
                  </div>
                </div>
                <div class="col-4">
                  <div class="card text-center py-2">
                    <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Flagged</p>
                    <h4 class="mb-0 text-danger">{{ vetResults.summary.items_flagged }}</h4>
                  </div>
                </div>
                <div class="col-4">
                  <div class="card text-center py-2">
                    <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Savings</p>
                    <h4 class="mb-0 text-info">&#8358;{{ vetResults.summary.savings | numeral(0, 0) }}</h4>
                  </div>
                </div>
              </div>

              <!-- AI Recommendation (Layer C) -->
              <div v-if="hivaReport && hivaReport.ai_recommendation" class="card mb-3"
                style="border-left: 4px solid #f6c23e;">
                <div class="card-body py-3">
                  <div class="d-flex align-items-center flex-wrap mb-2">
                    <span class="font-weight-bold mr-2" style="font-size: 0.82rem;">
                      <i class="fe fe-cpu mr-1 text-warning"></i> AI Recommendation
                    </span>
                    <span :class="{
                        'badge badge-soft-success mr-1': hivaReport.ai_recommendation.overall_recommendation === 'PASS',
                        'badge badge-soft-danger mr-1':  hivaReport.ai_recommendation.overall_recommendation === 'REJECT',
                        'badge badge-soft-warning mr-1': hivaReport.ai_recommendation.overall_recommendation === 'QUERY',
                      }" style="font-size: 0.72rem;">
                      {{ hivaReport.ai_recommendation.overall_recommendation }}
                    </span>
                    <span class="badge badge-soft-secondary" style="font-size: 0.72rem;">
                      {{ hivaReport.ai_recommendation.confidence }}
                    </span>
                  </div>
                  <div class="mb-2" style="font-size: 0.82rem; line-height: 1.6;"
                    v-html="parseMarkdown(hivaReport.ai_recommendation.narrative)"></div>
                  <div
                    v-if="hivaReport.ai_recommendation.key_concerns && hivaReport.ai_recommendation.key_concerns.length">
                    <p class="mb-1 font-weight-bold text-danger" style="font-size: 0.78rem;">Key Concerns:</p>
                    <ul class="mb-0 pl-3" style="font-size: 0.78rem;">
                      <li v-for="(concern, i) in hivaReport.ai_recommendation.key_concerns" :key="i"
                        class="text-danger">
                        {{ concern }}
                      </li>
                    </ul>
                  </div>

                </div>
              </div>

              <!-- Per-item results -->
              <div class="card mb-3" v-if="vetResults && !vetResults.external_api_raw">
                <div class="card-header py-2">
                  <span class="font-weight-bold" style="font-size: 0.85rem;">
                    <i class="fe fe-list text-info mr-1"></i> Item Results
                  </span>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm mb-0">
                    <thead class="bg-light">
                      <tr>
                        <th style="font-size: 0.78rem;">Item</th>
                        <th class="text-right" style="font-size: 0.78rem;">Submitted</th>
                        <th class="text-right" style="font-size: 0.78rem;">Suggested</th>
                        <th class="text-center" style="font-size: 0.78rem;">Confidence</th>
                        <th class="text-center" style="font-size: 0.78rem;">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in vetResults.results" :key="item.claims_service_id">
                        <td style="font-size: 0.82rem;">
                          <span :class="item.is_drug ? 'text-info' : ''">{{ item.item_name }}</span>
                          <span v-if="item.flags.length" class="d-block text-danger" style="font-size: 0.72rem;">
                            <i class="fe fe-alert-circle"></i> {{ item.flags.join('; ') }}
                          </span>
                        </td>
                        <td class="text-right" style="font-size: 0.82rem;">&#8358;{{ item.submitted_cost | numeral(0, 0)
                          }}</td>
                        <td class="text-right" style="font-size: 0.82rem;">
                          <span v-if="item.suggested_verified_price !== null">&#8358;{{ item.suggested_verified_price |
                            numeral(0, 0) }}</span>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td class="text-center" style="font-size: 0.78rem;">
                          <span v-if="item.historical_benchmark.confidence !== 'skipped'" :class="{
                              'badge badge-soft-success': item.historical_benchmark.confidence === 'high',
                              'badge badge-soft-warning': item.historical_benchmark.confidence === 'medium',
                              'badge badge-soft-danger': item.historical_benchmark.confidence === 'low' || item.historical_benchmark.confidence === 'none',
                            }">
                            {{ item.historical_benchmark.confidence }}
                          </span>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td class="text-center">
                          <span
                            :class="item.action === 'auto_approve' ? 'badge badge-soft-success' : 'badge badge-soft-danger'"
                            style="font-size: 0.72rem;">
                            {{ item.action === 'auto_approve' ? 'Auto-Approve' : 'Flag' }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>


              <!-- External Vetting Report (Layer C) -->
              <div v-if="hivaReport" class="mt-4">
                <div class="d-flex align-items-center mb-3">
                  <span class="font-weight-bold mr-2" style="font-size: 0.85rem;">
                    <i class="fe fe-shield mr-1 text-info"></i> AI Vetting Report
                  </span>
                  <span :class="{
                      'badge badge-soft-success': hivaReport.vetting_status === 'PASS',
                      'badge badge-soft-danger':  hivaReport.vetting_status === 'REJECT',
                      'badge badge-soft-warning': hivaReport.vetting_status === 'QUERY',
                    }" style="font-size: 0.78rem;">
                    {{ hivaReport.vetting_status }}
                  </span>
                  <small class="text-muted ml-auto" v-if="hivaView">Saved result · {{ hivaView.created_at }}</small>
                  <small class="text-muted ml-auto" v-else-if="vetResults && vetResults.hiva">
                    {{ vetResults.hiva.reused ? 'Saved result' : 'New result' }} · {{ vetResults.hiva.created_at }}
                  </small>
                </div>

                <div class="card mb-3" v-if="hivaReport.claim_summary">
                  <div class="card-body py-3">
                    <div class="row text-center">
                      <div class="col-4">
                        <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Total Submitted
                        </p>
                        <h5 class="mb-0">&#8358;{{ hivaReport.claim_summary.total_submitted_amount |
                          numeral(0, 0) }}</h5>
                      </div>
                      <div class="col-4">
                        <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Recommended</p>
                        <h5 class="mb-0 text-success">&#8358;{{
                          hivaReport.claim_summary.total_recommended_amount | numeral(0, 0) }}</h5>
                      </div>
                      <div class="col-4">
                        <p class="mb-1 text-muted" style="font-size: 0.7rem; text-transform: uppercase;">Overcharge</p>
                        <h5 class="mb-0 text-danger">
                          &#8358;{{ (hivaReport.claim_summary.total_submitted_amount -
                          hivaReport.claim_summary.total_recommended_amount) | numeral(0, 0) }}
                        </h5>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="card mb-3">
                  <div class="card-header py-2">
                    <span class="font-weight-bold" style="font-size: 0.82rem;">
                      <i class="fe fe-list text-info mr-1"></i> Checks Breakdown
                    </span>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm mb-0">
                      <thead class="bg-light">
                        <tr>
                          <th style="font-size: 0.75rem;">Check</th>
                          <th style="font-size: 0.75rem;">Item / Reason</th>
                          <th class="text-center" style="font-size: 0.75rem;">Status</th>
                          <th class="text-right" style="font-size: 0.75rem;">Submitted</th>
                          <th class="text-right" style="font-size: 0.75rem;">Approved</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(chk, i) in hivaReport.checks" :key="i">
                          <td style="font-size: 0.75rem; white-space: nowrap; text-transform: capitalize;">
                            {{ (chk.check_name || '').replace(/_/g, ' ') }}
                          </td>
                          <td style="font-size: 0.75rem;">
                            <span v-if="hivaLineName(chk)" class="font-weight-bold d-block">{{ hivaLineName(chk) }}</span>
                            <span class="text-muted" style="font-size: 0.7rem;">{{ chk.reason }}</span>
                          </td>
                          <td class="text-center">
                            <span :class="{
                                'badge badge-soft-success': chk.status === 'PASS',
                                'badge badge-soft-danger':  chk.status === 'FAIL',
                                'badge badge-soft-warning': chk.status === 'QUERY',
                              }" style="font-size: 0.7rem;">
                              {{ chk.status }}
                            </span>
                          </td>
                          <td class="text-right" style="font-size: 0.78rem;">
                            <span v-if="chk.submitted_value !== null && chk.submitted_value !== undefined">
                              &#8358;{{ chk.submitted_value | numeral(0, 0) }}
                            </span>
                            <span v-else class="text-muted">—</span>
                          </td>
                          <td class="text-right" style="font-size: 0.78rem;">
                            <span v-if="chk.recommended_value !== null && chk.recommended_value !== undefined"
                              :class="chk.status === 'FAIL' ? 'text-danger font-weight-bold' : 'text-success'">
                              &#8358;{{ chk.recommended_value | numeral(0, 0) }}
                            </span>
                            <span v-else class="text-muted">—</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div v-if="(hivaView || vetResults.hiva) && ((hivaView || vetResults.hiva).remark_codes || []).length" class="mb-2" style="font-size: 0.75rem;">
                  <span class="text-muted mr-1">Remark codes:</span>
                  <span v-for="code in (hivaView || vetResults.hiva).remark_codes" :key="code" class="badge badge-soft-secondary mr-1">{{ code }}</span>
                </div>
                <p class="text-muted text-right mb-0" style="font-size: 0.7rem;">
                  {{ hivaReport.ai_recommendation ? hivaReport.ai_recommendation.generated_by : 'HIVA TRACE' }}
                  <span v-if="hivaReport.metadata"> · {{
                    hivaReport.metadata.duration_ms }}ms</span>
                </p>
              </div>


              <button class="btn btn-success btn-block" @click="applyAutoVet" v-if="vetResults"
                :disabled="vetApplying || vetResults.summary.items_auto_approved === 0">
                <span v-if="vetApplying"><span class="spinner-border spinner-border-sm mr-1"></span> Applying…</span>
                <span v-else>
                  <i class="fe fe-check-circle mr-1"></i>
                  Apply {{ vetResults.summary.items_auto_approved }} Auto-Approved Price(s)
                </span>
              </button>
              <p class="text-muted text-center mt-2" style="font-size: 0.78rem;" v-if="vetResults">
                Only auto-approved items will be written. Flagged items require manual review.
              </p>




            </div>
          </div>
          <div class="modal-footer" style="border-top: 2px solid #e3eaf0;">
            <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Claim Summary Modal ── -->
    <div class="modal fade modal-slide-right" id="claimSummaryModal" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog scrollmodal" role="document" style="width: 520px; max-width: 100%;">
        <div class="modal-content">
          <div class="modal-header bg-soft-info" style="border-bottom: 2px solid #e3eaf0;">
            <div class="d-flex align-items-center">
              <div class="avatar avatar-sm mr-3">
                <div class="avatar-title rounded-circle bg-info text-white"><i class="fe fe-bar-chart-2"></i></div>
              </div>
              <h5 class="modal-title mb-0">Claim Summary Review</h5>
            </div>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="summaryLoading" class="text-center py-5">
              <div class="spinner-border text-info" role="status"></div>
              <p class="mt-2 text-muted">Analysing claim…</p>
            </div>
            <div v-else-if="claimSummary">
              <div v-if="claimSummary.duplicate_check.is_potential_duplicate"
                class="alert alert-danger d-flex align-items-start mb-3" role="alert">
                <i class="fe fe-alert-triangle mr-2 mt-1"></i>
                <div>
                  <strong>Potential Duplicate Detected</strong>
                  <ul class="mb-0 mt-1 pl-3">
                    <li v-for="(reason, i) in claimSummary.duplicate_check.reasons" :key="i">{{ reason }}</li>
                  </ul>
                </div>
              </div>
              <div class="card mb-3" style="border-left: 4px solid #5b8dee;">
                <div class="card-body py-3">
                  <p class="mb-1"
                    style="font-size: 0.78rem; text-transform: uppercase; letter-spacing: .05em; color: #888;">
                    <i class="fe fe-file-text mr-1"></i> Summary
                  </p>
                  <div style="line-height: 1.7;" v-html="parseMarkdown(claimSummary.narrative)"></div>
                </div>
              </div>
              <div class="row mb-3">
                <div class="col-6">
                  <div class="card text-center py-3">
                    <p class="mb-1 text-muted" style="font-size: 0.72rem; text-transform: uppercase;">Claims This Month
                    </p>
                    <h4 class="mb-0 text-info">{{ claimSummary.monthly_stats.total_claims_this_month }}</h4>
                    <p class="mb-0 text-muted" style="font-size: 0.78rem;">{{ claimSummary.monthly_stats.month }}</p>
                  </div>
                </div>
                <div class="col-6">
                  <div class="card text-center py-3">
                    <p class="mb-1 text-muted" style="font-size: 0.72rem; text-transform: uppercase;">Monthly Total</p>
                    <h4 class="mb-0 text-info">&#8358;{{ claimSummary.monthly_stats.total_amount_this_month | numeral(0,
                      0) }}</h4>
                    <p class="mb-0 text-muted" style="font-size: 0.78rem;">all claims combined</p>
                  </div>
                </div>
              </div>
              <div class="card mb-3" v-if="claimSummary.diagnosis_breakdown.length">
                <div class="card-header py-2">
                  <span class="font-weight-bold" style="font-size: 0.85rem;">
                    <i class="fe fe-activity text-info mr-1"></i> Diagnosis Breakdown (Past Month)
                  </span>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm mb-0">
                    <thead class="bg-light">
                      <tr>
                        <th style="font-size: 0.78rem;">Diagnosis</th>
                        <th class="text-center" style="font-size: 0.78rem;">Claims</th>
                        <th class="text-right" style="font-size: 0.78rem;">Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="d in claimSummary.diagnosis_breakdown" :key="d.diagnosis_id">
                        <td style="font-size: 0.82rem;">{{ d.diagnosis_name }}</td>
                        <td class="text-center" style="font-size: 0.82rem;">{{ d.count }}</td>
                        <td class="text-right" style="font-size: 0.82rem;">&#8358;{{ d.total_cost | numeral(0, 0) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-if="claimSummary.duplicate_check.same_diagnosis_this_month.length" class="card mb-3">
                <div class="card-header py-2">
                  <span class="font-weight-bold text-danger" style="font-size: 0.85rem;">
                    <i class="fe fe-copy mr-1"></i> Same Diagnosis This Month
                  </span>
                </div>
                <div class="list-group list-group-flush">
                  <div class="list-group-item px-3 py-2"
                    v-for="c in claimSummary.duplicate_check.same_diagnosis_this_month" :key="c.claim_id">
                    <a :href="`/claim/${c.claim_id}`" class="font-weight-bold text-info" target="_blank">{{
                      c.claim_unique_id }}</a>
                    <span class="mx-1 text-muted">·</span>
                    <span style="font-size: 0.82rem;">{{ c.provider }}</span>
                    <span class="mx-1 text-muted">·</span>
                    <span style="font-size: 0.82rem;">{{ c.date_submitted }}</span>
                    <span :class="`ml-2 badge badge-soft-${c.claim_status === 'approved' ? 'success' : 'warning'}`"
                      style="font-size: 0.72rem;">{{ c.claim_status }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted py-5">
              <i class="fe fe-bar-chart-2" style="font-size: 2rem;"></i>
              <p class="mt-2">Click "Summary Review" to analyse this claim.</p>
            </div>
          </div>
          <div class="modal-footer" style="border-top: 2px solid #e3eaf0;">
            <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import FraudSignals from "@/views/reports/components/FraudSignals.vue";
import ActivityLogPanel from "@/views/reports/components/ActivityLogPanel.vue";
import ServiceUsage from "./components/ServiceUsage.vue";
  import ClaimHistoryPanel from "./components/ClaimHistoryPanel.vue";
import Footer from "@/views/Footer.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { getClaimStatus } from "@/utils/claimStatus";

// NOTE: claimify-front's SingleClaim.vue uses `marked` (npm) for markdown
// rendering of AI narrative text. That package is not part of kgshia's
// package.json / node_modules, and per the sync house rules we do not
// fabricate/install a missing dependency here. `parseMarkdown` below is a
// small dependency-free fallback (escapes HTML, linebreaks, and basic
// **bold** / *italic* markup) that keeps the AI Vet / Claim Summary modals
// functional without requiring `npm install marked`.
function escapeHtml(str) {
  return str.replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}
function lightweightMarkdown(text) {
  if (!text) return "";
  let html = escapeHtml(text);
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
  html = html.replace(/\n/g, "<br>");
  return html;
}

import claimDraftMixin from "./mixins/claimDraftMixin";
  import claimReviewMixin from "./mixins/claimReviewMixin";

import { patientLink } from "@/utils/patientLink";
export default {
  components: { FraudSignals, ActivityLogPanel, ClaimHistoryPanel, ServiceUsage, Navbar, Footer, Loading },
  mixins: [claimDraftMixin, claimReviewMixin],
  data() {
    return {
      activityOpen: false,
      historyOpen: false,
      user: null,
      singleclaim: "",
      comments: "",
      remarks: "",
      remark_selected: "",
      shownotes: false,
      updateprice_vet: false,
      updateprice_ver: false,
      listprice: true,
      facility: "",
      clientdetail: "",
      selected_service: "",
      rejectingServiceId: "",
      body: "",
      modalVerdict: "",
      modalCommentBody: "",
      showManualPrice: false,
      calc: { dose: 1, frequency: 1, days: 1 },
      claimdetails: { paymentorders: [] },
      prepared_by: "",
      singlservicemodal: {
        service: { price: 0 },
        dose: 1,
        frequency: 1,
        days: 1,
      },
      show: false,
      isLoading: false,
      fullPage: true,
      agency_id: "",
      claimSummary: null,
      summaryLoading: false,
      vetLayers: ["A", "C"],
      vetResults: null,
      vetLoading: false,
      vetApplying: false,
      // HIVA TRACE (layer C): options sent with the analysis, and the claim's stored results.
      hivaCheckOptions: [
        { value: "price", label: "Price vs tariff" },
        { value: "dosage", label: "Dosage" },
        { value: "eligibility", label: "Eligibility" },
        { value: "drug_flags", label: "Drug flags" },
      ],
      hivaChecks: ["price", "dosage", "eligibility", "drug_flags"],
      hivaIncludeAi: true,
      hivaForce: false,
      hivaHistory: [],
      hivaHistoryLoading: false,
      hivaView: null,
    };
  },

  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.axios.get(`/api/v1/auth/remarks/95930`).then((response) => {
      this.remarks = response.data;
    });
  },

  computed: {
    // HIVA TRACE costs credit and sends claim data out, so the backend allows it for agency staff only.
    canUseHiva() {
      return !!this.user && (this.user.type === "shis" || this.user.type === "employee");
    },

    // The HIVA reply shown in the modal: the one just run, or a stored one picked from the history.
    hivaReport() {
      if (this.hivaView) return this.hivaView.response || null;
      return this.vetResults && this.vetResults.external_api_raw ? this.vetResults.external_api_raw : null;
    },

    claimStatus() {
      return getClaimStatus(this.claimdetails);
    },

    calculateTotal() {
      const unit_price =
        this.singlservicemodal.service != null
          ? this.singlservicemodal.service.price
          : this.singlservicemodal.drug.price;
      return unit_price * this.calc.dose * this.calc.frequency * this.calc.days;
    },
  },

  methods: {

    patientLink,
    parseMarkdown(text) {
      return lightweightMarkdown(text);
    },

    acceptServiceCost(service, type) {
      if (type === "vet") {
        service.vetted_price = service.cost;
      } else {
        service.verified_price = service.cost;
      }
      this.updateServicePrice(service);
    },

    acceptVetAmount(service) {
      service.verified_price = service.vetted_price;
      this.updateServicePrice(service);
    },

    async acceptAllVetAmounts() {
      if (!confirm("Set verified amount = vetted amount for all services?")) return;
      try {
        await Promise.all(
          this.singleclaim.services.map((s) =>
            this.axios.post("/api/v1/auth/claim_service/update-cost", {
              claims_service_id: s.id,
              vetted_price: s.vetted_price,
              verified_price: s.vetted_price,
            }).then(() => { s.verified_price = s.vetted_price; })
          )
        );
        this.getClaim();
        this.$toasted.info("All verified amounts updated!", { position: "top-center", duration: 3000 });
      } catch {
        this.$toasted.error("Some updates failed. Please try again.", { position: "top-center", duration: 4000 });
      }
    },

    remarkService(service) {
      this.selected_service = service.id;
    },

    startRejectService(service) {
      this.rejectingServiceId = service.id;
      this.remark_selected = "";
    },

    cancelRejectService() {
      this.rejectingServiceId = "";
      this.remark_selected = "";
    },

    confirmRejectService(service) {
      if (!this.remark_selected) {
        this.$toasted.error("Please select a remark for the rejection.", { position: "top-center", duration: 3000 });
        return;
      }
      if (!confirm("Reject this service? Vetted and verified amounts will be set to 0.")) return;

      this.axios
        .post(`/api/v1/auth/claim_service/update-cost`, {
          claims_service_id: service.id,
          vetted_price: 0,
          verified_price: 0,
        })
        .then(() =>
          this.axios.put(`/api/v1/auth/claim_service/${service.id}`, {
            user_id: this.user.id,
            claims_id: this.$route.params.id,
            services_id: service.services_id,
            drugs_id: service.drugs_id,
            cost: service.cost,
            remark_id: this.remark_selected,
          })
        )
        .then(() => {
          this.rejectingServiceId = "";
          this.remark_selected = "";
          this.getClaim();
          this.$toasted.success("Service rejected successfully!", { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.error(error);
          this.$toasted.error("Failed to reject service.", { position: "top-center", duration: 3000 });
        });
    },

    readycommentService(service) {
      this.selected_service = service.id;
      this.show = true;
    },

    getClaim() {
      this.isLoading = true;
      this.user = JSON.parse(localStorage.getItem("user"));
      return this.axios
        .get(`/api/v1/auth/detailedClaim/${this.$route.params.id}`)
        .then((response) => {
          this.singleclaim = response.data;
          this.comments = response.data.comments;
          this.claimdetails = response.data.singleclaim[0];
          this.axios
            .get(`/api/v1/auth/user/zam/${response.data.singleclaim[0].user_id}`)
            .then((res) => {
              this.prepared_by = res.data.user;
            });
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = true;
        });
    },

    // Re-point the open side-panel at its own service after a refetch, so
    // edits made from inside the panel (verdict, comment, price) show up
    // without the user having to close and reopen it.
    resyncServiceModal(serviceId) {
      if (!this.singleclaim || !this.singleclaim.services) return;
      const fresh = this.singleclaim.services.find((s) => s.id === serviceId);
      if (fresh) {
        this.singlservicemodal = fresh;
        this.calc = { dose: fresh.dose, frequency: fresh.frequency, days: fresh.days };
        this.modalVerdict = fresh.remark ? fresh.remark.id : "";
      }
    },

    addComment() {
      const payload =
        this.selected_service !== ""
          ? { user_id: this.user.id, claim_service_id: this.selected_service, body: this.body }
          : { user_id: this.user.id, claim_id: this.$route.params.id, body: this.body };

      this.axios.post(`/api/v1/auth/addComment`, payload).then(() => {
        this.getClaim();
        this.show = false;
        this.selected_service = "";
        this.body = "";
        this.$toasted.info("Added successfully!", { position: "top-center", duration: 3000 });
      });
    },

    updateService(service) {
      this.axios
        .put(`/api/v1/auth/claim_service/${service.id}`, {
          user_id: this.user.id,
          claims_id: this.$route.params.id,
          services_id: service.services_id,
          drugs_id: service.drugs_id,
          cost: service.cost,
          remark_id: this.remark_selected,
        })
        .then(() => {
          this.getClaim().then(() => this.resyncServiceModal(service.id));
          this.show = false;
          this.selected_service = "";
          this.remark_selected = "";
          this.$toasted.info("Updated successfully!", { position: "top-center", duration: 3000 });
        });
    },

    // Verdict + comment forms in the service side panel operate on
    // singlservicemodal directly, independent of the row-level dropdown state.
    saveModalVerdict() {
      if (!this.modalVerdict) {
        this.$toasted.error("Please select a verdict.", { position: "top-center", duration: 3000 });
        return;
      }
      const service = this.singlservicemodal;
      this.axios
        .put(`/api/v1/auth/claim_service/${service.id}`, {
          user_id: this.user.id,
          claims_id: this.$route.params.id,
          services_id: service.services_id,
          drugs_id: service.drugs_id,
          cost: service.cost,
          remark_id: this.modalVerdict,
        })
        .then(() => {
          this.getClaim().then(() => this.resyncServiceModal(service.id));
          this.$toasted.info("Verdict saved!", { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.error(error);
          this.$toasted.error("Failed to save verdict.", { position: "top-center", duration: 3000 });
        });
    },

    addModalComment() {
      if (!this.modalCommentBody.trim()) {
        this.$toasted.error("Please write a comment first.", { position: "top-center", duration: 3000 });
        return;
      }
      const service = this.singlservicemodal;
      this.axios
        .post(`/api/v1/auth/addComment`, {
          user_id: this.user.id,
          claim_service_id: service.id,
          body: this.modalCommentBody,
        })
        .then(() => {
          this.modalCommentBody = "";
          this.getClaim().then(() => this.resyncServiceModal(service.id));
          this.$toasted.info("Comment added!", { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.error(error);
          this.$toasted.error("Failed to add comment.", { position: "top-center", duration: 3000 });
        });
    },

    // Fills the given price field (vetted_price / verified_price) with
    // dose × frequency × days × unit price, then saves both price fields.
    applyCalcToPrice(field) {
      this.singlservicemodal[field] = this.calculateTotal;
      this.updateServicePrice(this.singlservicemodal);
    },

    // Vetting officers only: knocks 10% off a drug item's already-vetted
    // amount and logs an item comment recording both figures.
    applyDrugDeduction(service) {
      if (service.drug == null) {
        this.$toasted.error("The 10% deduction applies to drugs only.", { position: "top-center", duration: 3000 });
        return;
      }
      const vetted = Number(service.vetted_price);
      if (!vetted || vetted <= 0) {
        this.$toasted.error("Set the vetted amount before applying the deduction.", { position: "top-center", duration: 3000 });
        return;
      }
      const alreadyApplied = (service.servicecomments || []).some((c) => c.body && c.body.includes("10% drug deduction"));
      const prompt = alreadyApplied
        ? "A 10% deduction has already been applied to this item. Apply another 10% anyway?"
        : "Apply a 10% deduction to the vetted amount of this drug?";
      if (!confirm(prompt)) return;

      const deducted = Math.round(vetted * 0.9 * 100) / 100;
      const fmt = (n) => "₦" + n.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const body = `Vetted amount is ${fmt(vetted)}. A 10% drug deduction (${fmt(vetted - deducted)}) was applied, leading to ${fmt(deducted)}.`;

      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/claim_service/update-cost`, {
          claims_service_id: service.id,
          vetted_price: deducted,
          verified_price: service.verified_price,
        })
        .then(() =>
          this.axios.post(`/api/v1/auth/addComment`, {
            user_id: this.user.id,
            claim_service_id: service.id,
            body,
          })
        )
        .then(() => {
          this.$toasted.info("10% deduction applied!", { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.error(error);
          this.$toasted.error("Failed to apply the 10% deduction.", { position: "top-center", duration: 3000 });
        })
        .finally(() => {
          this.getClaim().then(() => this.resyncServiceModal(service.id));
        });
    },

    updateServicePrice(service) {
      this.axios
        .post(`/api/v1/auth/claim_service/update-cost`, {
          claims_service_id: service.id,
          vetted_price: service.vetted_price,
          verified_price: service.verified_price,
        })
        .then(() => {
          this.getClaim().then(() => this.resyncServiceModal(service.id));
          this.show = false;
          this.selected_service = "";
          this.$toasted.info("Cost updated successfully!", { position: "top-center", duration: 3000 });
        });
    },

    verifyClaim() {
      if (confirm("Are you sure you want to mark as verified?")) {
        this.axios
          .post(`/api/v1/auth/verify-claims/${this.$route.params.id}`, { claim_level: "Verification" })
          .then(() => {
            this.$router.push(`/all-claims`);
            this.$toasted.info("Verified successfully!", { position: "top-center", duration: 3000 });
          });
      }
    },

    vetClaim() {
      if (confirm("Are you sure you want to mark as vetted?")) {
        this.axios
          .post(`/api/v1/auth/checked-claims/${this.$route.params.id}`, { claim_level: "HMO" })
          .then(() => {
            this.getClaim();
            this.$router.push(`/all-claims`);
            this.$toasted.info("Vetted successfully!", { position: "top-center", duration: 3000 });
          });
      }
    },

    // Suspend (-1) and Reject (-2) both go through the same generic status
    // endpoint, unlike verify/vet which have their own dedicated endpoints.
    updateClaimStatus(status, actionLabel) {
      if (!confirm(`Are you sure you want to ${actionLabel} this claim?`)) return;
      this.axios
        .post(`/api/v1/auth/updateClaim/${this.$route.params.id}`, { status })
        .then(() => {
          this.getClaim();
          this.$toasted.info(`Claim ${actionLabel}ed successfully!`, { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.error(error);
          this.$toasted.error(`Failed to ${actionLabel} claim.`, { position: "top-center", duration: 3000 });
        });
    },

    getSingleService(service) {
      this.singlservicemodal = service;
      this.calc = { dose: service.dose, frequency: service.frequency, days: service.days };
      this.modalVerdict = service.remark ? service.remark.id : "";
      this.modalCommentBody = "";
      this.showManualPrice = false;
    },

    printMe() {
      const printContents = document.getElementById("printDiv").innerHTML;
      const originalContents = document.body.innerHTML;
      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
    },

    addDrugDeductable() {
      this.isLoading = true;
      this.axios
        .post("/api/v1/auth/claim_service", {
          services_id: 2019,
          claims_id: this.$route.params.id,
          days: 1,
          dose: 1,
          frequency: 1,
          verified_price: -this.singleclaim.revised_fee,
        })
        .then(() => {
          this.getClaim();
          this.isLoading = false;
          this.$breadstick.notify("Added successfully!", { position: "top-right" });
        })
        .catch((error) => {
          console.log(error.response);
          this.isLoading = false;
        });
    },

    fetchClaimSummary() {
      this.summaryLoading = true;
      this.claimSummary = null;
      this.axios
        .get(`/api/v1/auth/claims/${this.$route.params.id}/summary-review`)
        .then((response) => {
          this.claimSummary = response.data.data;
          this.summaryLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.summaryLoading = false;
          this.$toasted.error("Failed to load summary.", { position: "top-center", duration: 3000 });
        });
    },

    resetAutoVet() {
      this.vetResults = null;
      this.hivaView = null;
      this.hivaForce = false;
      if (!this.canUseHiva) {
        this.vetLayers = this.vetLayers.filter((l) => l !== "C");
        return;
      }
      this.loadHivaHistory();
    },

    loadHivaHistory() {
      if (!this.canUseHiva) return;
      this.hivaHistoryLoading = true;
      this.axios
        .get(`/api/v1/auth/hiva/claims/${this.$route.params.id}/vettings`)
        .then((response) => {
          this.hivaHistory = response.data.data || [];
        })
        .catch(() => {
          this.hivaHistory = [];
        })
        .finally(() => {
          this.hivaHistoryLoading = false;
        });
    },

    viewHivaResult(vetting) {
      this.hivaView = this.hivaView && this.hivaView.id === vetting.id ? null : vetting;
    },

    // Name of the claim line a HIVA check refers to (details.line_id = claims_services.id).
    hivaLineName(chk) {
      const details = chk.details || {};
      if (details.service_name || details.drug_name) return details.service_name || details.drug_name;
      const lineId = Number(chk.claims_service_id || details.line_id);
      const line = lineId && this.singleclaim && this.singleclaim.services
        ? this.singleclaim.services.find((s) => s.id === lineId)
        : null;
      if (!line) return "";
      return line.drug ? line.drug.drug_name : (line.service ? line.service.description : "");
    },

    hivaBadge(status) {
      const s = String(status || "").toUpperCase();
      if (s === "PASS") return "badge badge-soft-success";
      if (s === "REJECT" || s === "FAIL") return "badge badge-soft-danger";
      if (s === "QUERY") return "badge badge-soft-warning";
      return "badge badge-soft-secondary";
    },

    runAutoVet() {
      if (!this.vetLayers.length) {
        this.$toasted.error("Select at least one layer.", { position: "top-center", duration: 3000 });
        return;
      }
      const withHiva = this.vetLayers.includes("C");
      if (withHiva && !this.hivaChecks.length) {
        this.$toasted.error("Select at least one HIVA TRACE check.", { position: "top-center", duration: 3000 });
        return;
      }
      this.vetLoading = true;
      this.vetResults = null;
      this.hivaView = null;
      const params = { layers: this.vetLayers };
      if (withHiva) {
        params.hiva_checks = this.hivaChecks;
        params.hiva_include_ai = this.hivaIncludeAi ? 1 : 0;
        params.hiva_force = this.hivaForce ? 1 : 0;
      }
      this.axios
        .get(`/api/v1/auth/claims/${this.$route.params.id}/auto-vet`, { params })
        .then((response) => {
          this.vetResults = response.data.data;
          this.vetLoading = false;
          const hivaError = this.vetResults.external_api_error;
          if (hivaError) {
            this.$toasted.error(hivaError.message, { position: "top-center", duration: 6000 });
          } else if (this.vetResults.hiva && this.vetResults.hiva.reused) {
            this.$toasted.info("Showing the HIVA TRACE result from " + this.vetResults.hiva.created_at + " (claim unchanged).", { position: "top-center", duration: 4000 });
          }
          if (withHiva) this.loadHivaHistory();
        })
        .catch((error) => {
          console.error(error);
          this.vetLoading = false;
          const message = error.response && error.response.data && error.response.data.message;
          this.$toasted.error(message || "Auto-vetting failed. Please try again.", { position: "top-center", duration: 4000 });
        });
    },

    applyAutoVet() {
      if (!this.vetResults) return;
      this.vetApplying = true;
      this.axios
        .post(`/api/v1/auth/claims/${this.$route.params.id}/auto-vet/apply`, {
          results: this.vetResults.results,
        })
        .then(() => {
          this.vetApplying = false;
          this.getClaim();
          this.$toasted.success("Auto-approved prices applied successfully!", { position: "top-center", duration: 3000 });
          document.getElementById("autoVetModal").querySelector('[data-dismiss="modal"]').click();
        })
        .catch((error) => {
          console.error(error);
          this.vetApplying = false;
          this.$toasted.error("Failed to apply vetting results.", { position: "top-center", duration: 3000 });
        });
    },
  },

  created() {
    this.getClaim();
  },
};
</script>

<style scoped>
.hiva-options {
  background: #f8fafc;
  border: 1px solid #e3eaf0;
  border-radius: 6px;
  padding: 10px 12px;
}
/* ── Page Header ── */
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: #fff;
}
.header-icon-wrap {
  width: 52px; height: 52px; border-radius: 14px;
  background: rgba(255,255,255,.15);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.page-subtitle { opacity: .75; font-size: .9rem; }
.mt-n3 { margin-top: -16px; }

/* ── Action Toolbar Card ── */
.action-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
  margin-top: -20px;
}
.btn-submit {
  background: linear-gradient(135deg, #1e3a5f, #2563eb);
  color: #fff; border: none; border-radius: 8px;
  padding: 8px 18px; font-weight: 600; font-size: .82rem;
}
.btn-submit:hover { opacity: .9; color: #fff; }
.btn-tool {
  background: #f8fafc; border: 1px solid #e2e8f0;
  color: #374151; border-radius: 8px;
  padding: 6px 14px; font-size: .82rem; font-weight: 500;
}
.btn-tool:hover { background: #f1f5f9; color: #1e3a5f; }
.btn-accept-all {
  background: rgba(255,255,255,0.15);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.35);
  border-radius: 7px;
  font-size: .78rem;
  font-weight: 600;
  padding: 4px 12px;
  transition: background 0.15s;
}
.btn-accept-all:hover { background: rgba(255,255,255,0.28); color: #fff; }
.form-label { font-weight: 600; font-size: .85rem; color: #374151; margin-bottom: 6px; }

/* ── Status badges row ── */
.badge-soft-success { background: #d1fae5; color: #065f46; }
.badge-soft-danger  { background: #fee2e2; color: #991b1b; }
.badge-soft-warning { background: #fef3c7; color: #92400e; }
.badge-soft-info    { background: #dbeafe; color: #1d4ed8; }
.badge-soft-dark    { background: #e5e7eb; color: #1f2937; }
.badge-soft-draft { background: #f1f5f9; color: #475569; border: 1px dashed #94a3b8; }
.badge-soft-forwarded { background: #ede9fe; color: #5b21b6; }
.badge-soft-sent-back { background: #ffedd5; color: #9a3412; }

/* ── Send back / bulk actions ── */
.sent-back-alert { background: #fff7ed; border: 1px solid #fed7aa; color: #7c2d12; border-radius: 10px; }
.btn-outline-sent-back { border: 1px solid #ea580c; color: #c2410c; background: #fff; border-radius: 8px; font-weight: 600; }
.btn-outline-sent-back:hover { background: #fff7ed; color: #9a3412; }
.btn-sent-back { background: #ea580c; color: #fff; border: none; border-radius: 8px; font-weight: 600; }
.btn-sent-back:hover { background: #c2410c; color: #fff; }
.bulk-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 10px 16px; background: #eff6ff; border-bottom: 1px solid #bfdbfe; font-size: .85rem; }
tr.row-selected td { background: #eff6ff; }
.sb-backdrop { position: fixed; inset: 0; z-index: 1070; background: rgba(15, 23, 42, .55); display: flex; align-items: center; justify-content: center; padding: 16px; }
.sb-dialog { background: #fff; border-radius: 12px; width: 100%; max-width: 520px; overflow: hidden; box-shadow: 0 20px 50px rgba(0,0,0,.25); }
.sb-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; background: linear-gradient(135deg, #9a3412 0%, #ea580c 100%); }
.badge-soft-indigo  { background: #e0e7ff; color: #3730a3; }

/* ── Info Cards ── */
.info-card {
  border-radius: 14px;
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,.07);
  overflow: hidden;
}
.info-card-header {
  display: flex; align-items: center;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: #fff;
  font-weight: 700;
  font-size: .88rem;
  letter-spacing: .01em;
}

/* Enrollee photo section */
.info-card .text-center.py-3 {
  background: #f7f9ff;
  border-bottom: 1px solid #e9ecef;
}

/* List group rows */
.list-group-item {
  border-color: #f1f5f9;
}
.list-group-item p.mb-0.text-muted {
  font-size: .72rem;
  text-transform: uppercase;
  letter-spacing: .04em;
}

/* ── Services / Docs Tables ── */
.table thead th {
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: .75rem;
  text-transform: uppercase;
  letter-spacing: .06em;
  border-bottom: 2px solid #e2e8f0;
  padding: 12px 16px;
}
.table tbody td {
  padding: 11px 16px;
  vertical-align: middle;
  border-color: #f1f5f9;
  font-size: .88rem;
}
.table tbody tr:hover { background: #f8fafc; }
.table tfoot td,
.table tbody tr.bg-light { background: #f8fafc !important; font-weight: 700; }

/* ── Modals ── */
.scrollmodal { height: 100%; margin: 0; }
.scrollmodal .modal-content { height: 100%; display: flex; flex-direction: column; }
.scrollmodal .modal-body { overflow-y: auto; flex: 1; }

.modal-header.bg-soft-info {
  background: linear-gradient(135deg, #1e3a5f, #2563eb) !important;
  border-bottom: none !important;
}
.modal-header.bg-soft-info .modal-title { color: #fff; }
.modal-header.bg-soft-info .close { color: #fff; opacity: .8; }
.modal-header.bg-soft-info .close:hover { opacity: 1; }
.modal-header.bg-soft-info .avatar-title { background: rgba(255,255,255,.2) !important; }

/* Comments */
.media {
  background: #f8fafc !important;
  border-left: 3px solid #2563eb !important;
  border-radius: 8px;
}

/* Price calc accent */
.card[style*="border-left: 4px solid #5b8dee"] {
  border-left-color: #2563eb !important;
  border-radius: 10px;
}
</style>
